// `useMutacao`: o `useMutation` padrão do app (painel e aluno). Todo hook de escrita em api/** usa este, para
// que NENHUM erro de mutação passe em silêncio e o aviso de erro de rede/servidor seja sempre o mesmo.
//
// Regra do aviso (toast de erro, texto de `mensagemDeErro`):
//   - a tela lê `error`/`isError`/`failureReason` do resultado -> ela mostra o erro (no campo, numa faixa);
//     nada de toast;
//   - quem chamou passou `onError` no `mutate` (ou usa `mutateAsync` com try/catch) -> ele trata os erros de
//     regra (409, 422, 403...); o toast só aparece para erro de infraestrutura (sem conexão, 5xx, 429), DEPOIS
//     do tratamento dele (o texto padrão substitui um "Não deu, tente de novo" genérico);
//   - ninguém trata -> toast com a mensagem do servidor.
// Nunca avisa em 401/senha temporária (o cliente já leva ao login / troca de senha) nem com `meta.silencioso`
// (mutações de fundo, como "marcar como lido") ou `meta.erroNaTela` (a tela mostra TODO erro, inclusive 429/503 e sem
// conexão, na própria faixa: ex. senha própria, prancha 3.6; um toast a mais repetiria a mensagem).
import {
  useMutation,
  type DefaultError,
  type MutateOptions,
  type UseMutationOptions,
  type UseMutationResult,
} from "@tanstack/react-query";
import { useCallback, useRef } from "react";
import { useToast } from "../compartilhado/ui/Toast";
import { deveAvisarErro, mensagemDeErro } from "./erros";

declare module "@tanstack/react-query" {
  interface Register {
    mutationMeta: {
      /** Mutação de fundo: erro não gera toast (a tela não depende dela). */
      silencioso?: boolean;
      /** A tela mostra todos os erros desta mutação (inclusive os de infraestrutura): nada de toast global. */
      erroNaTela?: boolean;
    };
  }
}

const PROPS_DE_ERRO = new Set<PropertyKey>(["error", "isError", "failureReason"]);


export function useMutacao<TData = unknown, TError = DefaultError, TVariables = void, TOnMutateResult = unknown>(
  opcoes: UseMutationOptions<TData, TError, TVariables, TOnMutateResult>,
): UseMutationResult<TData, TError, TVariables, TOnMutateResult> {
  const toast = useToast();
  const resultado = useMutation(opcoes);
  const telaMostraErro = useRef(false);
  const silencioso = opcoes.meta?.silencioso === true || opcoes.meta?.erroNaTela === true;

  const avisar = useCallback(
    (erro: unknown, chamadorTrata: boolean) => {
      if (!deveAvisarErro(erro, { silencioso, telaMostraErro: telaMostraErro.current, chamadorTrata })) return;
      toast.erro(mensagemDeErro(erro));
    },
    [silencioso, toast],
  );

  type Resultado = UseMutationResult<TData, TError, TVariables, TOnMutateResult>;
  type Opcoes = MutateOptions<TData, TError, TVariables, TOnMutateResult>;
  // O TanStack tipa os parâmetros como tupla condicional (variáveis opcionais quando TVariables aceita
  // undefined); para envolver, a forma simples (v, op?) serve aos dois casos.
  const mutateOriginal = resultado.mutate as (v: TVariables, op?: Opcoes) => void;
  const mutateAsyncOriginal = resultado.mutateAsync as (v: TVariables, op?: Opcoes) => Promise<TData>;

  const mutate = useCallback(
    (variaveis: TVariables, op?: Opcoes) =>
      mutateOriginal(variaveis, {
        ...op,
        onError: (erro, ...resto) => {
          op?.onError?.(erro, ...resto);
          avisar(erro, op?.onError !== undefined);
        },
      }),
    [mutateOriginal, avisar],
  );

  const mutateAsync = useCallback(
    (variaveis: TVariables, op?: Opcoes) =>
      mutateAsyncOriginal(variaveis, op).catch((erro: unknown) => {
        avisar(erro, true);
        throw erro;
      }),
    [mutateAsyncOriginal, avisar],
  );

  // Mesmo objeto do TanStack, com mutate/mutateAsync envoltos e leitura de erro rastreada (como o próprio
  // TanStack faz com as props das consultas).
  const rastreado = {
    ...resultado,
    mutate: mutate as Resultado["mutate"],
    mutateAsync: mutateAsync as Resultado["mutateAsync"],
  } as Resultado;
  for (const prop of PROPS_DE_ERRO) {
    const valor = rastreado[prop as keyof Resultado];
    Object.defineProperty(rastreado, prop, {
      enumerable: true,
      get() {
        telaMostraErro.current = true;
        return valor;
      },
    });
  }
  return rastreado;
}
